const saleCalculator = require('../../../../sales/saleCalculator');
const dateUtil = require('../../../../../dateUtil/dateUtil');
const constants = require('../../../../constants');
const logger = require('../../../../../logger/logger');
const localizationUtils = require('../../../../localizationUtils');
const kenyaEndorsementUtil = require('../kenyaEndorsementUtil');


//#region Documentation

//
// KE = Country Code of Kenya
// KRA = Kenya Revenue Authority
// Endorsement handler for Kenya KRA saleEndorsement integration
// saleEndorsementHandler.applySaleEndorsement = add endorsement to sale.endorsements
// receiptEndorsementHandler.applyReceiptEndorsement = add endorsement related print lines to receipt
//

// Following API reference document provided by Pergamon was used to draw this handler
// REST API Strings and Endpoints - ExpressKitchen.docx
// Test URL Details:
// http://197.248.179.158:8084/api/sign?invoice{+idSuffix}
// Authorization: "Basic ZxZoaZMUQbUJDljA7kTExQ==2023"
// idSuffix = 1 ... #Item Price Inclusive of VAT
// idSuffix = 2 ... #Item Price Exclusive of VAT

//#endregion


export default {
    applySaleEndorsement: applySaleEndorsement,
    getSaleEndorsement: getSaleEndorsement
}

async function applySaleEndorsement(sale, integration)
{
    if (!kenyaEndorsementUtil.isSaleEndorsementApplicable(sale, integration))
        return;

    if (integration.jsonData.isDeferred && canDefer(sale)) 
    {
        sale.endorsements.push({
            integrationId: integration.id,
            integrationType: integration.integrationType,
            externalEntity: integration.externalEntity,
            isDeferred: true
        });
    }
    else
    {
        const endorsement = await getSaleEndorsement(sale, integration);

        if (endorsement != null)
        {
            sale.endorsements.push(endorsement);
        }
    }
}

function canDefer(sale)
{
    return sale.paymentMethod === constants.PaymentMethodCash;
}

async function getSaleEndorsement(sale, integration) 
{
    // Returns KE-KRA (Kenyan Revenue Authority) sale endorsement

    const isVatInclusive = localizationUtils.isVatInclusive();

    const serviceUrl = `${integration.jsonData.serviceUrl}${isVatInclusive ? "+1" : "+2"}`;     // KRA EDS serviceUrl
    const kraInvoice = getKRACompliantSaleInvoice(integration, sale, isVatInclusive);

    logger.info(`KE-KRA: getSaleEndorsement request - propertyId=${sale.propertyId}; SaleId/Number=${sale.id || 'new sale'}/'${sale.number}'; KRA Invoice = ${JSON.stringify(kraInvoice)}`);

    const request = {
        method: "POST",
        headers: getHttpHeaders(integration),
        body: JSON.stringify(kraInvoice)
    };

    const response = await fetch(serviceUrl, request);

    if (response.status < 200 || response.status > 299)
    {
        let text = await response.text();
        text = text || `Failed to get KRA endorsement with response status = ${response.status}`;
        console.log(`KRA: getSaleEndorsement http status error: status = ${response.status}, error = ${text}`);
        logger.error(`KRA: getSaleEndorsement http status error: status = ${response.status}, error = ${text}`);
        throw new Error(text);
    }

    const responseData = await response.json();

    const endorsement = {
        integrationId: integration.id,
        integrationType: integration.integrationType,
        externalEntity: integration.externalEntity,
        responseData: responseData,
        isError: typeof responseData.cu_invoice_number === "undefined"
    }

    if (endorsement.isError) 
    {
        logger.error(`KE-KRA: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }
    else
    {
        logger.info(`KE-KRA: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }

    return endorsement;

    //#region Sample responseData from KE-KRA Service

    /*

    Sample successful responseData

    {
        "invoice_number": "25022022112",                                    # This is your billing system's invoice number
        "cu_serial_number": "KRAMW009202108008133 12.XXX2023 XXX51:53",     # This is the control unit/device serial number
        "cu_invoice_number": "0090081330000093960",                         # This is the auto generated unique invoice number each time you print an invoice/credit note
        "verify_url": "https://tims-test.kra.go.ke/KRA-Portal/invoiceChk.htm?actionCode=loadPage&invoiceNo=0090081330000093960",    # This is the link used to create QR Code that is displayed on the receipt
        "description": "Signed successfully."
    }

    Sample failure responseData

    {
        "error_status": "Grand Total calculation error: -10.00 / 1150.00 1160.00 Amount Error"
    }

    */

    //#endregion
}

function getKRACompliantSaleInvoice(integration, sale, isVatInclusive)
{
    let totalNetAmount = 0;     // total sale amount exclusive of tax
    let totalVatAmount = 0;     // total vat amount

    const items_array = [];

    (function processSaleItems(saleItems)
    {
        for (const saleItem of saleItems)
        {
            // Ignore saleItems where unitPrice = 0

            if (saleItem.unitPrice > constants.numericThresholdValue) 
            {
                let unit_net_price, unit_gross_price;

                if (isVatInclusive)
                {
                    unit_net_price = saleItem.unitPrice - saleItem.vat;
                    unit_gross_price = saleItem.unitPrice;
                }
                else
                {
                    unit_net_price = saleItem.unitPrice;
                    unit_gross_price = saleItem.unitPrice;
                }

                items_array.push({
                    hscode: "",
                    name: saleItem.caption || saleItem.category || "N/A",
                    quantity: saleItem.quantity.toString(),
                    net_price: saleCalculator.roundNumericValue(unit_net_price).toString(),
                    unit_price: saleCalculator.roundNumericValue(unit_gross_price).toString(),
                });

                totalVatAmount += saleItem.quantity * saleItem.vat;
                totalNetAmount += saleItem.quantity * unit_net_price;
            }

            if (saleItem.saleItems.length > 0)
            {
                processSaleItems(saleItem.saleItems);   // Recursion
            }
        }
    })(sale.saleItems);     // IIF

    const totalDiscount = saleCalculator.calculateTotalDiscounts(sale);

    // Following invoice fields are being ignored as there are no matching fields in KRA invoice

    // const totalRefunds = saleCalculator.calculateTotalRefunds(sale);
    // const totalCharges = saleCalculator.calculateTotalCharges(sale);
    // totalVatAmount += saleCalculator.calculateTotalChargesVAT(sale);

    // Draw KRA Invoice

    const grand_total = saleCalculator.roundNumericValue(totalNetAmount + totalVatAmount);
    const net_subtotal = saleCalculator.roundNumericValue(totalNetAmount);
    const tax_total = saleCalculator.roundNumericValue(totalVatAmount);
    const net_discount_total = saleCalculator.roundNumericValue(totalDiscount);

    const invoice = {

        invoice_date: dateUtil.formatDate(dateUtil.convertToLocalStandard(sale.dateTime), dateUtil.internalDateFormat),
        invoice_number: sale.number.toString(),

        invoice_pin: integration.jsonData.invoicePin,           // Restaurant KRA Registration number (mandatory)
        customer_pin: "",                                       // Customer/Buyer's KRA Registration number (optional)
        customer_exid: "",                                      // Customer/Buyer's KRA tax exemption id (optional)

        grand_total: grand_total.toString(),                    // total invoice amount inclusive of tax (mandatory)
        net_subtotal: net_subtotal.toString(),                  // total invoice amount exclusive of tax (optional)
        tax_total: tax_total.toString(),                        // total tax amount (mandatory)

        net_discount_total: net_discount_total.toString(),

        sel_currency: integration.jsonData.selectedCurrency,
        rel_doc_number: "",

        items_array: items_array
    }

    return invoice;
}

function getHttpHeaders(integration)
{
    return ({
        "Content-Type": "application/json",
        "Authorization": `Basic ${integration.jsonData.authorizationKey}`
    });
}
